import { useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  buildSerpSeasonEndContentHtml,
  SerpSeasonEndSnapshot,
} from '@/components/Approval/@components/SerpSeasonEnd';
import { getSerpSeasonEndDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import {
  buildSerpSeasonEndSubject,
  SERP_SEASON_END_DETAIL_TYPE_CODE,
  type SerpSeasonEndDetail,
  type SerpSeasonEndDraftSource,
  type SerpSeasonEndSource,
} from '@/utils/approval/serpSeasonEnd';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpSeasonEndFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes 의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
};

const SWR_OPTIONS = { revalidateOnFocus: false, shouldRetryOnError: false };

/**
 * SERP 시즌마감 조정(174) — 레거시 appr_edit_forms_serp_season_end.
 * 본문 조회가 끝나면 정산내역(매장별)을 이어서 조회. 편집도 다시 조회하고 제목도 덮어씀(레거시 동일)
 */
const SerpSeasonEndField = ({ serpDocument }: SerpSeasonEndFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpSeasonEnd' });
  const subjectFilledRef = useRef(false);
  const notifiedErrorRef = useRef<unknown>(null);

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const main = useSWR<SerpSeasonEndDraftSource<SerpSeasonEndSource>>(
    typeCode && sequence
      ? getSerpSeasonEndDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    SWR_OPTIONS,
  );
  const detail = useSWR<SerpSeasonEndDraftSource<SerpSeasonEndDetail>>(
    main.data && sequence
      ? getSerpSeasonEndDraftSourceUrl(
          SERP_SEASON_END_DETAIL_TYPE_CODE,
          sequence,
        )
      : null,
    getNestedDataFetcher,
    SWR_OPTIONS,
  );

  useEffect(() => {
    const current = getValues('serpSeasonEnd');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpSeasonEnd', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!main.data || !typeCode || !sequence) return;
    if (
      main.data.attributes.typeCode !== typeCode ||
      main.data.attributes.sequence !== sequence
    )
      return;
    const source = main.data.display.source ?? {};
    const detailSource = detail.data?.display.source ?? null;
    const autoSubject =
      getValues('serpSeasonEnd')?.autoSubject ??
      buildSerpSeasonEndSubject(source, t);
    setValue(
      'serpSeasonEnd',
      {
        typeCode,
        sequence,
        source,
        detail: detailSource,
        detailSettled: Boolean(detail.data || detail.error),
        autoSubject,
      },
      { shouldDirty: false },
    );
    if (subjectFilledRef.current) return;
    subjectFilledRef.current = true;
    setValue('subject', autoSubject, { shouldDirty: false });
  }, [
    main.data,
    detail.data,
    detail.error,
    typeCode,
    sequence,
    getValues,
    setValue,
    t,
  ]);

  useEffect(() => {
    // snackbar 는 렌더마다 새 객체 — 같은 오류로 다시 띄우면 무한 갱신
    if (!detail.error || notifiedErrorRef.current === detail.error) return;
    notifiedErrorRef.current = detail.error;
    snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
  }, [detail.error, snackbar, t]);

  const content = useMemo(
    () =>
      state?.source
        ? buildSerpSeasonEndContentHtml(state.source, state.detail, t)
        : null,
    [state?.source, state?.detail, t],
  );

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (content) {
      return (
        <>
          {!state?.detailSettled && (
            <ApprovalNoticeBox role="status" sx={{ mb: 1 }}>
              {t('데이터를 불러오는 중입니다...')}
            </ApprovalNoticeBox>
          )}
          <SerpSeasonEndSnapshot content={content} />
        </>
      );
    }
    if (main.error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (main.isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return <FieldBlock className="serp-season-end">{renderBody()}</FieldBlock>;
};

export default SerpSeasonEndField;
